import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";

/**
 * Handler for the inline "Cookie-Einstellungen"-CTA within the privacy text.
 * Dispatches the same event the Footer button uses, so the CookieConsentBanner
 * re-opens and the user can withdraw their consent (Art. 7 Abs. 3 DSGVO).
 */
function reopenCookieBanner() {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent("feaststar:cookie-reopen"));
}

export default function Datenschutz() {
  useEffect(() => {
    document.title = "Datenschutzerklärung · Feaststar";
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-bg">
      <Nav variant="home" />
      <main className="legal-page" data-testid="datenschutz-page">
        <div className="max-w-[760px] mx-auto px-5 lg:px-6 pt-28 lg:pt-32 pb-24">
          <Link to="/" className="legal-back" data-testid="legal-back">← Zurück zur Startseite</Link>

          <h1 className="display-md mt-6 mb-8">
            <span className="block italic text-accent">Datenschutzerklärung</span>
          </h1>

          <p className="body-md text-muted mb-10">
            Diese Datenschutzerklärung informiert Sie darüber, welche personenbezogenen Daten wir auf dieser Website verarbeiten und welche Rechte Ihnen nach der Datenschutz-Grundverordnung (DSGVO), dem Bundesdatenschutzgesetz (BDSG) und dem Digitale-Dienste-Gesetz (DDG) zustehen.
          </p>

          {/* 1 */}
          <section className="legal-section">
            <h2 className="legal-h2">1. Datenschutz auf einen Blick</h2>
            <p className="body-md">
              Verantwortlich für die Datenverarbeitung auf dieser Website:
            </p>
            <p className="body-md mt-3">
              Jessie James Irven<br />
              Greiters 392<br />
              87764 Legau<br />
              E-Mail: <a href="mailto:kontakt@feaststar.app" className="legal-link">kontakt@feaststar.app</a><br />
              Telefon: <a href="tel:+491718178286" className="legal-link">+49 171 8178286</a>
            </p>
          </section>

          {/* 2 */}
          <section className="legal-section">
            <h2 className="legal-h2">2. Allgemeine Hinweise und Pflichtinformationen</h2>
            <p className="body-md">
              Wir verarbeiten personenbezogene Daten ausschließlich im Rahmen der gesetzlichen Bestimmungen — insbesondere der DSGVO, des BDSG und des DDG. Personenbezogene Daten sind Informationen, die sich auf eine identifizierte oder identifizierbare natürliche Person beziehen (z. B. Ihr Name, Ihre E-Mail-Adresse oder Ihre IP-Adresse).
            </p>
            <p className="body-md mt-3">
              <strong>Widerruf Ihrer Einwilligung:</strong> Sie können eine erteilte Einwilligung jederzeit mit Wirkung für die Zukunft widerrufen (Art. 7 Abs. 3 DSGVO). Die Rechtmäßigkeit der bis zum Widerruf erfolgten Verarbeitung bleibt unberührt. Eine formlose Mitteilung per E-Mail an{" "}
              <a href="mailto:kontakt@feaststar.app" className="legal-link">kontakt@feaststar.app</a> genügt.
            </p>
            <p className="body-md mt-3">
              <strong>Beschwerderecht bei der Aufsichtsbehörde:</strong> Sie haben das Recht, sich bei einer Datenschutzaufsichtsbehörde zu beschweren (Art. 77 DSGVO). Zuständig für uns ist:
            </p>
            <p className="body-md mt-3">
              Bayerisches Landesamt für Datenschutzaufsicht (BayLDA)<br />
              Promenade 18<br />
              91522 Ansbach<br />
              Web: <a href="https://www.lda.bayern.de" target="_blank" rel="noreferrer" className="legal-link">www.lda.bayern.de</a>
            </p>
          </section>

          {/* 3 */}
          <section className="legal-section">
            <h2 className="legal-h2">3. Datenerfassung auf dieser Website</h2>

            <h3 className="legal-h3">a) Cookies und lokale Speicherung</h3>
            <p className="body-md">
              Wir verwenden ausschließlich technisch notwendige Speicherung im Browser (LocalStorage) sowie einen Cookie-Banner zur Einholung Ihrer Einwilligung. Ihre Einwilligung können Sie jederzeit über den Link{" "}
              <button
                type="button"
                onClick={reopenCookieBanner}
                data-testid="datenschutz-cookie-reopen"
                className="legal-link"
                style={{ background: "transparent", border: 0, padding: 0, cursor: "pointer" }}
              >
                „Cookie-Einstellungen"
              </button>
              {" "}im Footer widerrufen.
            </p>
            <p className="body-md mt-2">
              Rechtsgrundlage: § 25 Abs. 2 Nr. 2 TTDSG (technisch unbedingt erforderlich) bzw. Art. 6 Abs. 1 lit. a DSGVO (Einwilligung) für optionale Funktionen.
            </p>

            <h3 className="legal-h3 mt-6">b) Server-Log-Files</h3>
            <p className="body-md">
              Beim Aufruf unserer Website werden automatisch technische Daten (Browsertyp, Betriebssystem, Referrer-URL, Datum/Uhrzeit des Zugriffs, gekürzte bzw. gehashte IP-Adresse) erfasst. Diese Daten dienen ausschließlich dem sicheren Betrieb der Website und werden nach kurzer Zeit anonymisiert oder gelöscht.
            </p>
            <p className="body-md mt-2">
              Rechtsgrundlage: Art. 6 Abs. 1 lit. f DSGVO (berechtigtes Interesse am sicheren Betrieb).
            </p>

            <h3 className="legal-h3 mt-6">c) Newsletter / Warteliste (Launch-Benachrichtigung)</h3>
            <p className="body-md">
              Wenn Sie sich für die Launch-Benachrichtigung anmelden, verarbeiten wir Ihre E-Mail-Adresse (Pflicht) sowie optional Ihre Telefonnummer und Ihre Einwilligung zum Newsletter.
            </p>
            <p className="body-md mt-2">
              Rechtsgrundlage: Art. 6 Abs. 1 lit. a DSGVO (Einwilligung). Sie können Ihre Einwilligung jederzeit per E-Mail an{" "}
              <a href="mailto:kontakt@feaststar.app" className="legal-link">kontakt@feaststar.app</a> widerrufen — wir löschen Ihre Daten dann unverzüglich.
            </p>

            <h3 className="legal-h3 mt-6">d) Partner-Kontaktformular (für Restaurants)</h3>
            <p className="body-md">
              Wenn Sie als Restaurant, Café, Bäckerei oder Hofladen über das Partner-Formular Kontakt aufnehmen, verarbeiten wir Restaurantname, Ansprechpartner, E-Mail, Telefon, Stadt und die von Ihnen ausgewählten Ernährungsstandards.
            </p>
            <p className="body-md mt-2">
              Rechtsgrundlage: Art. 6 Abs. 1 lit. b DSGVO (Vertragsanbahnung).
            </p>
          </section>

          {/* 4 — Art. 9 DSGVO Health data */}
          <section className="legal-section legal-section-highlight" data-testid="datenschutz-art9">
            <h2 className="legal-h2">4. Besondere Kategorie: gesundheits­bezogene Daten (Art. 9 DSGVO)</h2>
            <p className="body-md">
              Feaststar verarbeitet Ernährungspräferenzen (z. B. vegan, glutenfrei, laktosefrei, bio, regional). Diese Daten können Rückschlüsse auf den Gesundheits­zustand ermöglichen und gelten daher als <strong>besondere Kategorien personen­bezogener Daten</strong> im Sinne von Art. 9 Abs. 1 DSGVO.
            </p>
            <p className="body-md mt-3">
              <strong>Rechtsgrundlage:</strong> Ihre ausdrückliche Einwilligung gemäß Art. 9 Abs. 2 lit. a DSGVO.
            </p>
            <p className="body-md mt-2">
              <strong>Zweck:</strong> Personalisiertes Restaurant-Matching auf Basis Ihrer Ernährungs­präferenzen.
            </p>
            <p className="body-md mt-2">
              <strong>Speicherdauer:</strong> Bis zum Widerruf Ihrer Einwilligung oder Löschung Ihres Accounts.
            </p>
            <p className="body-md mt-2">
              <strong>Widerruf:</strong> Sie können Ihre Einwilligung jederzeit mit Wirkung für die Zukunft widerrufen. Die Rechtmäßigkeit der bis zum Widerruf erfolgten Verarbeitung bleibt unberührt. Ein formloser Widerruf per E-Mail an{" "}
              <a href="mailto:kontakt@feaststar.app" className="legal-link">kontakt@feaststar.app</a> genügt.
            </p>
          </section>

          {/* 5 */}
          <section className="legal-section">
            <h2 className="legal-h2">5. Hosting und Content Delivery</h2>
            <p className="body-md">
              Diese Website wird gehostet durch <strong>Emergent Agent</strong> (App-Hosting) und nutzt <strong>MongoDB Atlas</strong> als Cloud-Datenbank. Beide Anbieter verarbeiten Daten innerhalb der Europäischen Union.
            </p>
            <p className="body-md mt-2">
              Mit unseren Hosting-Providern haben wir Auftragsverarbeitungs­verträge nach Art. 28 DSGVO geschlossen. Eine Weitergabe Ihrer Daten an Dritte außerhalb dieser Auftragsverarbeitung findet nicht statt.
            </p>
          </section>

          {/* 6 */}
          <section className="legal-section">
            <h2 className="legal-h2">6. Analyse-Tools und Tracking</h2>
            <p className="body-md">
              Ohne Ihre Einwilligung nutzen wir <strong>keine Analyse-Cookies</strong>. Ein selbstgehostetes, technisch notwendiges Tracking (QR-Scan- und Page-View-Zählung) speichert IP-Adressen ausschließlich in <strong>gehashter Form</strong> zur Sicherheit und Missbrauchs-Prävention.
            </p>
            <p className="body-md mt-2">
              Rechtsgrundlage für dieses technisch notwendige Tracking: Art. 6 Abs. 1 lit. f DSGVO (berechtigtes Interesse an sicherem Betrieb und Produktverbesserung).
            </p>

            <h3 className="legal-h3 mt-6">Google Analytics 4 (nur mit Einwilligung)</h3>
            <p className="body-md">
              Wenn Sie im Cookie-Banner „Akzeptieren" wählen, setzen wir zusätzlich <strong>Google Analytics 4</strong> (Anbieter: Google Ireland Limited, Gordon House, Barrow Street, Dublin 4, Irland) zur pseudonymisierten Reichweitenmessung ein. Google Analytics setzt Cookies und übermittelt Nutzungsdaten (z. B. gerufene Seiten, ungefähre Region auf Basis der IP-Adresse, Gerätetyp) an Google.
            </p>
            <p className="body-md mt-2">
              <strong>Mess-ID:</strong> G-142KLGVXS2
            </p>
            <p className="body-md mt-2">
              <strong>Zweck:</strong> Reichweitenmessung, Verbesserung der Website und Analyse anonymer Nutzungsmuster.
            </p>
            <p className="body-md mt-2">
              <strong>Rechtsgrundlage:</strong> Ihre Einwilligung gemäß <strong>Art. 6 Abs. 1 lit. a DSGVO</strong> in Verbindung mit § 25 Abs. 1 TTDSG.
            </p>
            <p className="body-md mt-2">
              <strong>Datenschutz-Vorkehrungen:</strong> Wir setzen den <strong>Google Consent Mode v2</strong> ein. Vor Ihrer Zustimmung werden keine Analyse-Cookies gesetzt und keine Messdaten an Google übermittelt. Die IP-Adresse wird zusätzlich anonymisiert (<code>anonymize_ip</code>).
            </p>
            <p className="body-md mt-2">
              <strong>Speicherdauer:</strong> Standard-Speicherdauer der Google-Analytics-Cookies (in der Regel bis zu 14 Monate). Server-seitige Ereignisdaten werden gemäß Google-Standard­einstellungen aufbewahrt.
            </p>
            <p className="body-md mt-2">
              <strong>Datenübermittlung in Drittländer:</strong> Bei der Nutzung von Google Analytics können Daten in die USA übermittelt werden. Google hat sich dem EU-U.S. Data Privacy Framework unterworfen (Angemessenheitsbeschluss der EU-Kommission vom 10.07.2023).
            </p>
            <p className="body-md mt-2">
              <strong>Widerruf:</strong> Sie können Ihre Einwilligung jederzeit mit Wirkung für die Zukunft widerrufen, indem Sie im Footer auf{" "}
              <button
                type="button"
                onClick={reopenCookieBanner}
                data-testid="datenschutz-ga-cookie-reopen"
                className="legal-link"
                style={{ background: "transparent", border: 0, padding: 0, cursor: "pointer" }}
              >
                „Cookie-Einstellungen"
              </button>
              {" "}klicken und Ihre Auswahl ändern. Die Rechtmäßigkeit der bis zum Widerruf erfolgten Verarbeitung bleibt unberührt.
            </p>
            <p className="body-md mt-2">
              Weitere Informationen zum Datenschutz bei Google Analytics finden Sie unter{" "}
              <a href="https://policies.google.com/privacy?hl=de" target="_blank" rel="noreferrer" className="legal-link">
                policies.google.com/privacy
              </a>.
            </p>
          </section>

          {/* 7 */}
          <section className="legal-section">
            <h2 className="legal-h2">7. Ihre Rechte</h2>
            <p className="body-md">Sie haben jederzeit das Recht auf:</p>
            <ul className="legal-list">
              <li>Auskunft (Art. 15 DSGVO)</li>
              <li>Berichtigung unrichtiger Daten (Art. 16 DSGVO)</li>
              <li>Löschung Ihrer Daten (Art. 17 DSGVO)</li>
              <li>Einschränkung der Verarbeitung (Art. 18 DSGVO)</li>
              <li>Datenübertragbarkeit (Art. 20 DSGVO)</li>
              <li>Widerspruch (Art. 21 DSGVO)</li>
              <li>Widerruf erteilter Einwilligungen (Art. 7 Abs. 3 DSGVO)</li>
            </ul>
            <p className="body-md mt-3">
              Zur Ausübung Ihrer Rechte schreiben Sie einfach eine E-Mail an{" "}
              <a href="mailto:kontakt@feaststar.app" className="legal-link">kontakt@feaststar.app</a>.
            </p>
          </section>

          {/* 8 */}
          <section className="legal-section">
            <h2 className="legal-h2">8. Datensicherheit</h2>
            <p className="body-md">
              Wir setzen technische und organisatorische Maßnahmen ein, um Ihre Daten zu schützen: <strong>SSL/TLS-Verschlüsselung</strong> (erkennbar am Schloss-Symbol Ihres Browsers und der „https://"-Adresszeile), <strong>gehashte IP-Adressen</strong>, sowie <strong>bcrypt-verschlüsselte Passwörter</strong> für alle Administrations-Zugänge.
            </p>
          </section>

          <p className="body-sm text-muted mt-12">Stand: Juli 2026</p>
        </div>
      </main>
      <Footer variant="home" />
    </div>
  );
}
